/**
 * The served-market inner pages of the institutional site, faithful to the authoritative Claude Design
 * `Site Institucional.dc.html` (the home page follows v2, `HomePage.tsx`): S02 Motoristas, S03 Vello Business, S04 Segurança — plus the pages the design's
 * nav and footer point to and did not draw (help, about, press, careers, sign in, download), composed from the same
 * primitives. Every string is the market's injected `SiteContent`; every destination is a pack `SiteTarget` resolved by
 * {@link SiteLinkTo}; every colour is a `@vello/tokens` variable.
 *
 * The photo and screenshot slots of the design are still placeholders (real photography comes later): they render the
 * Vello mark on the mist surface, never the designer's note.
 */
import { type ReactElement, useId, useState } from 'react';
import { currencyMinorUnits } from '@vello/format';
import markOnLight from '@vello/brand/assets/simbolo/svg/vello-mark-on-light.svg';
import appIcon from '@vello/brand/assets/app-icon/vello-appicon-navy.png';
import type { SiteBusinessPage, SiteCallout, SiteContent, SiteDownloadPage, SiteDriversPage, SiteFeature, SiteHelpPage, SiteSafetyPage, SiteSignInPage, SiteStoreLinks, SiteTextPage } from '@vello/web-entry';
import { simulatorEstimateMinor } from '../src/site-links.js';
import { SiteLinkTo, useSiteNav } from './SiteChrome';
import { Icon } from './icons';

/** A photo/screenshot slot still waiting for its image: the mist surface with the Vello mark. */
export function ArtSlot({ className }: { className: string }): ReactElement {
  return (
    <div className={`art ${className}`} aria-hidden="true">
      <img className="art-mark" src={markOnLight} alt="" />
    </div>
  );
}

/** The two store badges of an app: a store link when the app is published there, else the download page. */
export function StoreBadges({ stores, appStore, googlePlay, className, fallback }: { stores: SiteStoreLinks | undefined; appStore: string; googlePlay: string; className: string; fallback: string }): ReactElement {
  return (
    <>
      <SiteLinkTo target={stores?.appStore ?? fallback} className={className}>{appStore}</SiteLinkTo>
      <SiteLinkTo target={stores?.googlePlay ?? fallback} className={className}>{googlePlay}</SiteLinkTo>
    </>
  );
}

function FeatureCard({ feature, row = false }: { feature: SiteFeature; row?: boolean }): ReactElement {
  return (
    <div className={row ? 'feature-card feature-card--row' : 'feature-card'}>
      <span className={feature.tone === 'danger' ? 'feature-icon feature-icon--danger' : 'feature-icon'}>
        <Icon name={feature.icon} size={row ? 28 : 26} />
      </span>
      <div className="feature-copy">
        <h3 className="feature-title">{feature.title}</h3>
        <p className="feature-body">{feature.body}</p>
      </div>
    </div>
  );
}

/** A closing band: ink (driver), mist (safety) or outlined. One action. */
export function Callout({ callout, tone }: { callout: SiteCallout; tone: 'ink' | 'mist' | 'outline' }): ReactElement {
  return (
    <section className={`callout callout--${tone}`}>
      <div className="callout-copy">
        <h2 className="callout-title">{callout.title}</h2>
        <p className="callout-body">{callout.body}</p>
      </div>
      <SiteLinkTo target={callout.cta.target} className={tone === 'ink' ? 'btn btn--inverse btn--lg' : 'btn btn--primary btn--lg'}>
        {callout.cta.label}
      </SiteLinkTo>
    </section>
  );
}

// ---- S02 Motoristas ---------------------------------------------------------------------------------------------------

function EarningsSimulator({ sim }: { sim: NonNullable<SiteDriversPage['simulator']> }): ReactElement {
  const { locale } = useSiteNav();
  const [hours, setHours] = useState(Math.min(sim.maxHours, Math.max(sim.minHours, sim.defaultHours)));
  const sliderId = useId();
  const minorPerMajor = 10 ** currencyMinorUnits(sim.currency);
  // A daily slider (D628): the caption shows the week's hours, the result covers `resultDays` days (a month).
  const weekHours = hours * (sim.daysPerWeek ?? 1);
  const estimate = simulatorEstimateMinor(hours * (sim.resultDays ?? 1), sim.hourlyMinor, minorPerMajor) / minorPerMajor;
  let amount: string;
  try {
    amount = new Intl.NumberFormat(locale, { style: 'currency', currency: sim.currency, maximumFractionDigits: 0 }).format(estimate);
  } catch {
    amount = `${sim.currency} ${estimate}`;
  }
  const context = sim.context.replace('{hours}', String(hours)).replace('{weekHours}', String(weekHours));
  return (
    <div id="earnings" className="sim-card">
      <span className="eyebrow">{sim.eyebrow}</span>
      {sim.title !== undefined && <h2 className="sim-title">{sim.title}</h2>}
      {sim.support !== undefined && <p className="sim-support">{sim.support}</p>}
      <div className="sim-field">
        <label className="sim-label" htmlFor={sliderId}>{sim.hoursLabel}</label>
        <input
          id={sliderId}
          className="sim-range"
          type="range"
          min={sim.minHours}
          max={sim.maxHours}
          step={1}
          value={hours}
          aria-valuetext={context}
          onChange={(e) => setHours(Number(e.target.value))}
        />
        <span className="sim-context tnum">{context}</span>
      </div>
      <div className="sim-result">
        <span className="sim-result-label">{sim.resultLabel}</span>
        <output className="sim-amount tnum" htmlFor={sliderId} aria-live="polite">{amount}</output>
        <span className="sim-note">{sim.note}</span>
        {sim.disclaimer !== undefined && <span className="sim-disclaimer">{sim.disclaimer}</span>}
      </div>
    </div>
  );
}

export function DriversPage({ page: p }: { page: SiteDriversPage }): ReactElement {
  return (
    <>
      <section className="band band--mist">
        <div className="band-inner page-hero page-hero--wide">
          <div className="page-hero-copy">
            <h1 className="page-title">{p.title}</h1>
            <p className="page-sub">{p.subtitle}</p>
            <div className="hero-cta">
              <SiteLinkTo target={p.ctaPrimary.target} className="btn btn--primary btn--lg">{p.ctaPrimary.label}</SiteLinkTo>
              <SiteLinkTo target={p.ctaSecondary.target} className="btn btn--outline btn--lg">{p.ctaSecondary.label}</SiteLinkTo>
            </div>
          </div>
          {p.simulator !== undefined && <EarningsSimulator sim={p.simulator} />}
        </div>
      </section>

      <section className="band-inner feature-grid">
        {p.features.map((f) => <FeatureCard key={f.title} feature={f} />)}
      </section>

      <section className="band-inner split">
        <div className="split-copy">
          <h2 id="requirements" className="section-title">{p.requirementsTitle}</h2>
          <ul className="check-list">
            {p.requirements.map((r) => (
              <li key={r} className="check-item">
                <span className="check-icon"><Icon name="check-circle" size={20} /></span>
                {r}
              </li>
            ))}
          </ul>
          <h2 className="section-title section-title--sm">{p.stepsTitle}</h2>
          <ol className="steps">
            {p.steps.map((s, i) => (
              <li key={s.title} className="step">
                <span className="step-num tnum" aria-hidden="true">{i + 1}</span>
                <div>
                  <div className="step-title">{s.title}</div>
                  <div className="step-body">{s.body}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <ArtSlot className="split-art" />
      </section>

      <div className="band-inner band-bottom">
        <Callout callout={p.closing} tone="ink" />
      </div>
    </>
  );
}

// ---- S03 Vello Business -----------------------------------------------------------------------------------------------

export function BusinessPage({ page: p }: { page: SiteBusinessPage }): ReactElement {
  return (
    <>
      <section className="band-inner page-hero">
        <div className="page-hero-copy">
          <span className="eyebrow">{p.eyebrow}</span>
          <h1 className="page-title">{p.title}</h1>
          <p className="page-sub">{p.subtitle}</p>
          <div className="hero-cta">
            <SiteLinkTo target={p.ctaPrimary.target} className="btn btn--primary btn--lg">{p.ctaPrimary.label}</SiteLinkTo>
            <SiteLinkTo target={p.ctaSecondary.target} className="btn btn--secondary btn--lg">{p.ctaSecondary.label}</SiteLinkTo>
          </div>
        </div>
        <ArtSlot className="page-hero-art" />
      </section>

      <section id="features" className="band-inner feature-grid feature-grid--tight">
        {p.features.map((f) => <FeatureCard key={f.title} feature={f} />)}
      </section>

      <div className="band-inner band-bottom">
        <section className="fact-band">
          <div className="fact-band-copy">
            <h2 className="fact-band-title">{p.band.title}</h2>
            <p className="fact-band-body">{p.band.body}</p>
          </div>
          <div className="fact-band-facts">
            {p.band.facts.map((f) => (
              <div key={f.label}>
                <div className="fact-value tnum">{f.value}</div>
                <div className="fact-label">{f.label}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

// ---- S04 Segurança ----------------------------------------------------------------------------------------------------

export function SafetyPage({ page: p }: { page: SiteSafetyPage }): ReactElement {
  return (
    <>
      <section className="band band--ink">
        <div className="band-inner safety-hero">
          <span className="safety-hero-icon"><Icon name="shield" size={56} /></span>
          <h1 className="page-title safety-title">{p.title}</h1>
          <p className="page-sub safety-sub">{p.subtitle}</p>
        </div>
      </section>
      <section className="band-inner feature-grid feature-grid--2">
        {p.features.map((f) => <FeatureCard key={f.title} feature={f} row />)}
      </section>
      <div className="band-inner band-bottom">
        <Callout callout={p.report} tone="mist" />
      </div>
    </>
  );
}

// ---- Help -------------------------------------------------------------------------------------------------------------

export function HelpPage({ page: p }: { page: SiteHelpPage }): ReactElement {
  const { widgetEnabled } = useSiteNav();
  return (
    <div className="band-inner text-page">
      <div className="text-head">
        <h1 className="page-title page-title--md">{p.title}</h1>
        <p className="page-sub">{p.subtitle}</p>
      </div>
      <section className="callout callout--outline">
        <span className="feature-icon"><Icon name="message" size={28} /></span>
        <div className="callout-copy">
          <h2 className="callout-title">{p.contact.title}</h2>
          <p className="callout-body">{widgetEnabled ? p.contact.body : p.contactFallback}</p>
        </div>
        {widgetEnabled && <SiteLinkTo target={p.contact.cta.target} className="btn btn--primary btn--lg">{p.contact.cta.label}</SiteLinkTo>}
      </section>
      <section className="faq">
        <h2 className="section-title section-title--sm">{p.faqTitle}</h2>
        {p.faq.map((item) => (
          <details key={item.question} className="faq-item">
            <summary className="faq-q">
              <span>{item.question}</span>
              <span className="faq-chevron"><Icon name="chevron-down" size={20} /></span>
            </summary>
            <p className="faq-a">{item.answer}</p>
          </details>
        ))}
      </section>
      {p.more.length > 0 && (
        <nav className="link-row" aria-label={p.title}>
          {p.more.map((l) => (
            <SiteLinkTo key={l.target} target={l.target} className="text-link">{l.label}</SiteLinkTo>
          ))}
        </nav>
      )}
    </div>
  );
}

// ---- About / Press / Careers ------------------------------------------------------------------------------------------

export function TextPage({ page: p }: { page: SiteTextPage }): ReactElement {
  return (
    <div className="band-inner text-page">
      <div className="text-head">
        <span className="eyebrow">{p.eyebrow}</span>
        <h1 className="page-title page-title--md">{p.title}</h1>
      </div>
      <div className="prose">
        {p.paragraphs.map((para) => <p key={para}>{para}</p>)}
      </div>
      {p.highlights !== undefined && p.highlights.length > 0 && (
        <section className="feature-grid feature-grid--flush">
          {p.highlights.map((f) => <FeatureCard key={f.title} feature={f} />)}
        </section>
      )}
      {p.cta !== undefined && <Callout callout={p.cta} tone="mist" />}
    </div>
  );
}

// ---- Sign in ----------------------------------------------------------------------------------------------------------

export function SignInPage({ page: p }: { page: SiteSignInPage }): ReactElement {
  return (
    <div className="band-inner text-page text-page--wide">
      <div className="text-head">
        <h1 className="page-title page-title--md">{p.title}</h1>
        <p className="page-sub">{p.subtitle}</p>
      </div>
      <div className="option-grid">
        {p.options.map((o) => (
          <div key={o.title} className="option-card">
            <span className="feature-icon"><Icon name={o.icon} size={26} /></span>
            <h2 className="feature-title">{o.title}</h2>
            <p className="feature-body">{o.body}</p>
            <SiteLinkTo target={o.cta.target} className="btn btn--secondary option-cta">{o.cta.label}</SiteLinkTo>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Download ---------------------------------------------------------------------------------------------------------

export function DownloadPage({ page: p, content: c }: { page: SiteDownloadPage; content: SiteContent }): ReactElement {
  const { widgetEnabled } = useSiteNav();
  const contactLabel = widgetEnabled ? (c.pages?.help?.contact.cta.label ?? c.navHelp) : c.navHelp;
  const anyPending = p.apps.some((a) => c.stores?.[a.id]?.appStore === undefined || c.stores?.[a.id]?.googlePlay === undefined);
  return (
    <div className="band-inner text-page text-page--wide">
      <div className="text-head">
        <h1 className="page-title page-title--md">{p.title}</h1>
        <p className="page-sub">{p.subtitle}</p>
      </div>
      <div className="app-grid">
        {p.apps.map((app) => {
          const stores = c.stores?.[app.id];
          return (
            <section key={app.id} id={app.id} className="app-card">
              <img className="app-card-icon" src={appIcon} alt="" aria-hidden="true" />
              <span className="eyebrow">{app.eyebrow}</span>
              <h2 className="feature-title">{app.title}</h2>
              <p className="feature-body">{app.body}</p>
              <div className="download-badges">
                {stores?.appStore !== undefined
                  ? <SiteLinkTo target={stores.appStore} className="btn btn--primary btn--sm">{c.download.appStore}</SiteLinkTo>
                  : <span className="store-badge store-badge--pending" aria-disabled="true">{c.download.appStore}</span>}
                {stores?.googlePlay !== undefined
                  ? <SiteLinkTo target={stores.googlePlay} className="btn btn--primary btn--sm">{c.download.googlePlay}</SiteLinkTo>
                  : <span className="store-badge store-badge--pending" aria-disabled="true">{c.download.googlePlay}</span>}
              </div>
            </section>
          );
        })}
      </div>
      {anyPending && (
        <section className="callout callout--mist" role="note">
          <div className="callout-copy">
            <p className="callout-body">{p.pending}</p>
          </div>
          {contactLabel !== undefined && <SiteLinkTo target="contact" className="btn btn--primary btn--lg">{contactLabel}</SiteLinkTo>}
        </section>
      )}
    </div>
  );
}
